import { useRef } from 'react'
import type { Doc, Version, UpdateMetadata } from '@w/crdt'

export function containsVersion(version: Version, other: Version) {
  return version.epoch === other.epoch && [...other.vector].every(([id, clock]) => (version.vector.get(id) ?? 0) >= clock)
}

/** Exclude only received blocks already integrated; preserve this editor's unsaved author clocks. */
export function receivedLiveVersion(update: UpdateMetadata, applied: Version): Version {
  const vector = new Map<number, number>()
  if (update.epoch !== applied.epoch) return { epoch: applied.epoch, vector }
  for (const block of update.blocks) {
    const client = Number(block.client)
    if (!Number.isSafeInteger(client) || String(client) !== block.client) throw new Error('Unsafe live client identity')
    const end = Math.min(block.start + block.length, applied.vector.get(client) ?? 0)
    vector.set(client, Math.max(vector.get(client) ?? 0, end))
  }
  return { epoch: applied.epoch, vector }
}

function mergeVersion(current: Version, written: Version): Version {
  const vector = new Map(current.vector)
  for (const [id, clock] of written.vector) vector.set(id, Math.max(vector.get(id) ?? 0, clock))
  return { epoch: written.epoch, vector }
}

/** Settles a task into its page error source (page-errors.ts `track`). */
type Track = (task: Promise<unknown>) => Promise<unknown>

function createPageWrites(doc: Doc) {
  // The document version this page has already written. One typing burst is one
  // write of everything authored since it, however many changes the burst holds.
  let committed = { doc, version: doc.version() }
  let deviceVersion = committed.version
  let importing = Promise.resolve()
  let comments: Promise<unknown> = Promise.resolve()
  let pendingComments = 0
  let entries: Promise<void>[] = []
  return {
    /** The cursor follows the session doc, which is replaced on an authoritative rewind. */
    follow(session: Doc) { if (committed.doc !== session) { committed = { doc: session, version: session.version() }; deviceVersion = committed.version } },
    isCurrent: (session: Doc) => committed.doc === session,
    since: () => committed.version,
    deviceEdit(version: Version) { deviceVersion = version },
    canPublishLive: () => containsVersion(committed.version, deviceVersion),
    /** Merges a written version into the cursor of a still-current session. */
    advance(session: Doc, written: Version) {
      if (committed.doc === session) committed = { doc: session, version: mergeVersion(committed.version, written) }
    },
    /** After a remote import the session's whole version is written. */
    resync(session: Doc) { committed = { doc: session, version: session.version() } },
    importing(task: Promise<void>) { importing = task; return task },
    afterImport: () => importing,
    /** Queues a comment write after the previous ones. */
    comment(run: () => Promise<unknown>, track: Track) {
      pendingComments += 1
      comments = track(comments.then(run)).finally(() => { pendingComments -= 1 })
      return comments
    },
    /** Unmount flush: waits for queued comments first, so they still reach the registered save. */
    afterComments: <T>(flush: () => Promise<T>) => pendingComments > 0 ? comments.then(flush) : flush(),
    entry(pending: Promise<void>, track: Track) {
      entries.push(pending)
      void track(pending).finally(() => { entries = entries.filter(task => task !== pending) })
      return pending
    },
    /** Settles every pending page write: queued comments, the space flush, the latest import, entry writes. */
    async settled(flush: () => Promise<unknown>) {
      await comments
      const settling = entries
      await Promise.all([flush(), importing, ...settling])
      entries = entries.filter(change => !settling.includes(change))
    },
  }
}

/**
 * The open page's pending writes, one owner per state (page-writes.model.md): the write cursor
 * of the session `doc`, the latest remote import, the comment queue and in-flight entry writes.
 * The page instance is keyed per page, so none of this outlives the page it describes.
 */
export function usePageWrites(doc: Doc) {
  const writes = useRef<ReturnType<typeof createPageWrites> | null>(null)
  // oxlint-disable-next-line react/refs -- one stable write owner per page instance; its cursor follows the session doc replaced during render
  writes.current ??= createPageWrites(doc)
  // oxlint-disable-next-line react/refs -- same owner as above
  writes.current.follow(doc)
  // oxlint-disable-next-line react/refs -- same owner as above
  return writes.current
}
